// @flow
import React, { type Node, useCallback } from 'react';

import Loading from 'app/common/components/Loader';
import SimpleDate from 'app/dataProviders/SimpleDate';

import useKlasses from '../useKlasses';
import KlassSwitcher from '../KlassSwitcher';
import useKlassValid from '../useKlassValid';
import useDateValid from '../useDateValid';
import useRedirect from '../useRedirect';
import Dnevnik from './Dnevnik';
import UndefinedDate from '../UndefinedDate';
import ControlsLayout from '../ControlsLayout';

import WeekSwitcher from './WeekSwitcher';
import useAvailableWeeks from './useAvailableWeeks';


type Props = {
  studentId: string,
  date?: string,
  dateFormat: string,
  klassId?: string,
  additionalControls?: Node,
  redirect: ({ +date?: string, +klassId: string }, writeHistory?: boolean) => void,
}

const Home = ({
  studentId,
  date,
  dateFormat,
  klassId,
  additionalControls,
  redirect,
}: Props): Node => {
  const [klasses] = useKlasses(studentId);

  const validatedKlass = useKlassValid(klassId, klasses);
  const validatedDate = useDateValid({ date, dateFormat, klass: validatedKlass, alignByWeek: true });
  const { changeKlass, changeDate } = useRedirect({
    klassId: validatedKlass ? validatedKlass.id : null,
    date: validatedDate,
    redirect,
    dateFormat,
  });

  const { onPrevWeek, onNextWeek } = useAvailableWeeks({
    date: validatedDate,
    changeDate,
    klass: validatedKlass,
    dateFormat,
  });

  const klassIdValid = validatedKlass ? validatedKlass.id : null;

  const checkDateDisableStatus = useCallback((dayFormatted: string, format: string, u?: string) => {
    const checkableDate = new SimpleDate(dayFormatted, format);
    const unit = SimpleDate.getSimpleDateUnit(u);
    if (validatedKlass) {
      const { schoolyearPeriods } = validatedKlass;
      return !(schoolyearPeriods || []).find(
        ({ start, finish }) => checkableDate.isBetween(start, finish, unit)
      );
    }
    return false;
  }, [validatedKlass]);

  if (!klassIdValid) {
    return (<Loading />);
  }

  if (!validatedDate) {
    return (
      <UndefinedDate>
        {klasses.length > 1
          && <ControlsLayout leftContent={additionalControls}>
            <KlassSwitcher
              klasses={klasses}
              klassId={klassIdValid}
              onChangeKlass={changeKlass}
            />
          </ControlsLayout>}
      </UndefinedDate>
    );
  }

  return (
    <Dnevnik
      studentId={studentId}
      date={validatedDate}
      klassId={klassIdValid}
      additionalControls={additionalControls}
      onPrevWeek={onPrevWeek}
      onNextWeek={onNextWeek}
    >
      <KlassSwitcher
        klasses={klasses}
        klassId={klassIdValid}
        onChangeKlass={changeKlass}
      />
      <WeekSwitcher
        date={validatedDate}
        onChangeDate={changeDate}
        dateDisabledStatus={checkDateDisableStatus}
      />
    </Dnevnik>
  );
};

export default Home;
